/** Cliente API — Materialidad (módulo aislado dentro de Trámites). */
import api from "@/lib/api";

/**
 * Normaliza periodo desde camelCase (buildPeriodQuery) o snake_case legacy.
 * @returns {{ period?: string, period_from?: string, period_to?: string }}
 */
export function normalizeMaterialidadPeriodParams(input = {}) {
  const period = input.period || undefined;
  const periodFrom = input.periodFrom || input.period_from || undefined;
  const periodTo = input.periodTo || input.period_to || undefined;
  const out = {};
  if (period) out.period = period;
  if (periodFrom) out.period_from = periodFrom;
  if (periodTo) out.period_to = periodTo;
  return out;
}

export async function fetchMaterialidadCatalog() {
  const { data } = await api.get("/tramites/materialidad/catalog");
  return data;
}

export async function fetchMaterialidadRiskCriteria() {
  const { data } = await api.get("/tramites/materialidad/risk-criteria");
  return data;
}

export async function fetchMaterialidadConfig(clientId) {
  const { data } = await api.get(`/tramites/materialidad/config/${encodeURIComponent(clientId)}`);
  return data;
}

export async function saveMaterialidadConfig(clientId, payload) {
  const { data } = await api.put(`/tramites/materialidad/config/${encodeURIComponent(clientId)}`, payload);
  return data;
}

export async function fetchMaterialidadFaltantes({
  period,
  periodFrom,
  periodTo,
  period_from,
  period_to,
  clientId,
  /** false = solo lectura; true = incorporar checklist / sync al pedir Actualizar. */
  syncCfdi = false,
} = {}) {
  const params = {
    ...normalizeMaterialidadPeriodParams({ period, periodFrom, periodTo, period_from, period_to }),
  };
  if (clientId) params.client_id = clientId;
  if (syncCfdi === false) params.sync_cfdi = false;
  const { data } = await api.get("/tramites/materialidad/faltantes", { params });
  return data;
}

export async function syncMaterialidadCfdi(clientId, periodQuery = {}, { force = false } = {}) {
  const params = { force, ...normalizeMaterialidadPeriodParams(periodQuery) };
  const { data } = await api.post(
    `/tramites/materialidad/sync-cfdi/${encodeURIComponent(clientId)}`,
    null,
    { params },
  );
  return data;
}

/** Detecta CFDI en todas las empresas con actividad en el periodo. */
export async function syncMaterialidadCfdiBatch(periodQuery = {}, { force = true, maxClients = 80 } = {}) {
  const params = {
    force,
    max_clients: maxClients,
    ...normalizeMaterialidadPeriodParams(periodQuery),
  };
  const { data } = await api.post("/tramites/materialidad/sync-cfdi", null, { params, timeout: 300000 });
  return data;
}

export async function fetchMaterialidadResumen({
  period,
  periodFrom,
  periodTo,
  period_from,
  period_to,
  clientId,
} = {}) {
  const params = {
    ...normalizeMaterialidadPeriodParams({ period, periodFrom, periodTo, period_from, period_to }),
  };
  if (clientId) params.client_id = clientId;
  const { data } = await api.get("/tramites/materialidad/resumen", { params });
  return data;
}

export async function fetchMaterialidadEfosHallazgos({ year, clientId } = {}) {
  const params = {};
  if (year != null && year !== "") params.year = Number(year);
  if (clientId) params.client_id = clientId;
  const { data } = await api.get("/tramites/materialidad/efos-hallazgos", { params });
  return data;
}

/** Recalcula cruce EFOS (lista 69-B vs CFDI); no es Analizar riesgos CFDI. */
export async function refreshMaterialidadEfosHallazgos({
  year,
  clientId,
  maxClients = 80,
} = {}) {
  const params = { max_clients: maxClients };
  if (year != null && year !== "") params.year = Number(year);
  if (clientId) params.client_id = clientId;
  const { data } = await api.post("/tramites/materialidad/efos-hallazgos/refresh", null, {
    params,
    timeout: 300000,
  });
  return data;
}

function appendPeriodParams(params, input = {}) {
  const normalized = normalizeMaterialidadPeriodParams(input);
  if (normalized.period) params.set("period", normalized.period);
  if (normalized.period_from) params.set("period_from", normalized.period_from);
  if (normalized.period_to) params.set("period_to", normalized.period_to);
}

export function materialidadFaltantesPdfUrl(input = {}) {
  const params = new URLSearchParams();
  appendPeriodParams(params, input);
  if (input.clientId) params.set("client_id", input.clientId);
  const qs = params.toString();
  return `/api/tramites/materialidad/export/faltantes.pdf${qs ? `?${qs}` : ""}`;
}

export function materialidadResumenPdfUrl(input = {}) {
  const params = new URLSearchParams();
  appendPeriodParams(params, input);
  if (input.clientId) params.set("client_id", input.clientId);
  const qs = params.toString();
  return `/api/tramites/materialidad/export/resumen.pdf${qs ? `?${qs}` : ""}`;
}

export function materialidadCartaClientePdfUrl(input = {}) {
  const params = new URLSearchParams();
  appendPeriodParams(params, input);
  if (input.clientId) params.set("client_id", input.clientId);
  if (input.includeResolved) params.set("include_resolved", "true");
  if (input.riskFilter) params.set("risk_filter", input.riskFilter);
  if (input.relationshipType) params.set("relationship_type", input.relationshipType);
  const qs = params.toString();
  return `/api/tramites/materialidad/export/carta-cliente.pdf?${qs}`;
}

export function materialidadCartaClienteXlsxUrl(input = {}) {
  const params = new URLSearchParams();
  appendPeriodParams(params, input);
  if (input.clientId) params.set("client_id", input.clientId);
  if (input.includeResolved) params.set("include_resolved", "true");
  if (input.riskFilter) params.set("risk_filter", input.riskFilter);
  if (input.relationshipType) params.set("relationship_type", input.relationshipType);
  const qs = params.toString();
  return `/api/tramites/materialidad/export/carta-cliente.xlsx?${qs}`;
}

export async function ensureMaterialidadPeriod(clientId, period) {
  const params = period ? { period } : {};
  const { data } = await api.post(
    `/tramites/materialidad/periods/${encodeURIComponent(clientId)}/ensure`,
    null,
    { params },
  );
  return data;
}

export async function patchMaterialidadItem(itemId, payload) {
  const { data } = await api.patch(`/tramites/materialidad/item/${encodeURIComponent(itemId)}`, payload);
  return data;
}

export async function uploadMaterialidadEvidence(itemId, file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post(`/tramites/materialidad/item/${encodeURIComponent(itemId)}/upload`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export async function linkMaterialidadTask(itemId, despachoTaskId) {
  const { data } = await api.post(`/tramites/materialidad/item/${encodeURIComponent(itemId)}/link-task`, {
    despacho_task_id: despachoTaskId,
  });
  return data;
}

export function materialidadFileDownloadUrl(fileId) {
  return `/api/tramites/materialidad/files/${encodeURIComponent(fileId)}`;
}

export async function fetchMaterialidadCfdiPreview(clientId, cfdiUuid) {
  const { data } = await api.get("/tramites/materialidad/cfdi-preview", {
    params: { client_id: clientId, uuid: cfdiUuid },
  });
  return data;
}

export async function fetchMaterialidadItem(itemId) {
  const { data } = await api.get(`/tramites/materialidad/item/${encodeURIComponent(itemId)}`);
  return data;
}

/** Expediente por contraparte (Fase A) — no altera faltantes ni sync-cfdi de riesgos. */

export async function syncMaterialidadCounterparties(clientId, periodQuery = {}) {
  const params = {
    client_id: clientId,
    ...normalizeMaterialidadPeriodParams(periodQuery),
  };
  const { data } = await api.post("/tramites/materialidad/counterparties/sync", null, {
    params,
    timeout: 180000,
  });
  return data;
}

export async function fetchMaterialidadCounterparties({
  clientId,
  period,
  periodFrom,
  periodTo,
  period_from,
  period_to,
  relationshipType,
  status,
  q,
  onlyWithPeriodCfdi = false,
} = {}) {
  const params = {
    client_id: clientId,
    ...normalizeMaterialidadPeriodParams({ period, periodFrom, periodTo, period_from, period_to }),
  };
  if (relationshipType) params.relationship_type = relationshipType;
  if (status) params.status = status;
  if (q) params.q = q;
  if (onlyWithPeriodCfdi) params.only_with_period_cfdi = true;
  const { data } = await api.get("/tramites/materialidad/counterparties", { params });
  return data;
}

export async function fetchMaterialidadExpediente(counterpartyId, periodQuery = {}) {
  const params = normalizeMaterialidadPeriodParams(periodQuery);
  const { data } = await api.get(
    `/tramites/materialidad/counterparties/${encodeURIComponent(counterpartyId)}/expediente`,
    { params },
  );
  return data;
}

export async function classifyMaterialidadCounterparty(counterpartyId, payload) {
  const { data } = await api.patch(
    `/tramites/materialidad/counterparties/${encodeURIComponent(counterpartyId)}`,
    payload,
  );
  return data;
}

export async function uploadMaterialidadCounterpartyDocument(
  counterpartyId,
  file,
  { documentType, scope, issuedAt, validUntil, notes } = {},
) {
  const fd = new FormData();
  fd.append("file", file);
  const params = { document_type: documentType };
  if (scope) params.scope = scope;
  if (issuedAt) params.issued_at = issuedAt;
  if (validUntil) params.valid_until = validUntil;
  if (notes) params.notes = notes;
  const { data } = await api.post(
    `/tramites/materialidad/counterparties/${encodeURIComponent(counterpartyId)}/documents`,
    fd,
    {
      params,
      headers: { "Content-Type": "multipart/form-data" },
    },
  );
  return data;
}

/** Operaciones MAT-* por CFDI (Fase B) — no altera SAT ni Faltantes. */

export async function syncMaterialidadOperations(counterpartyId, periodQuery = {}) {
  const params = normalizeMaterialidadPeriodParams(periodQuery);
  const { data } = await api.post(
    `/tramites/materialidad/counterparties/${encodeURIComponent(counterpartyId)}/operations/sync`,
    null,
    { params, timeout: 180000 },
  );
  return data;
}

export async function fetchMaterialidadOperations(counterpartyId, periodQuery = {}) {
  const params = normalizeMaterialidadPeriodParams(periodQuery);
  const { data } = await api.get(
    `/tramites/materialidad/counterparties/${encodeURIComponent(counterpartyId)}/operations`,
    { params },
  );
  return data;
}

export async function openMaterialidadOperationByUuid(counterpartyId, cfdiUuid, periodQuery = {}) {
  const params = normalizeMaterialidadPeriodParams(periodQuery);
  const { data } = await api.post(
    `/tramites/materialidad/counterparties/${encodeURIComponent(counterpartyId)}/operations/by-uuid/${encodeURIComponent(cfdiUuid)}`,
    null,
    { params },
  );
  return data;
}

export async function fetchMaterialidadOperation(caseId) {
  const { data } = await api.get(
    `/tramites/materialidad/operations/${encodeURIComponent(caseId)}`,
  );
  return data;
}

export async function patchMaterialidadOperation(caseId, payload) {
  const { data } = await api.patch(
    `/tramites/materialidad/operations/${encodeURIComponent(caseId)}`,
    payload,
  );
  return data;
}

export async function uploadMaterialidadOperationDocument(
  caseId,
  file,
  { documentType, issuedAt, validUntil, notes } = {},
) {
  const fd = new FormData();
  fd.append("file", file);
  const params = { document_type: documentType };
  if (issuedAt) params.issued_at = issuedAt;
  if (validUntil) params.valid_until = validUntil;
  if (notes) params.notes = notes;
  const { data } = await api.post(
    `/tramites/materialidad/operations/${encodeURIComponent(caseId)}/documents`,
    fd,
    {
      params,
      headers: { "Content-Type": "multipart/form-data" },
    },
  );
  return data;
}

/** Export carpeta ZIP/PDF/correo (Fase C) — no altera SAT ni Faltantes. */

function materialidadPeriodQueryString(periodQuery = {}) {
  const params = new URLSearchParams();
  const normalized = normalizeMaterialidadPeriodParams(periodQuery);
  Object.entries(normalized).forEach(([k, v]) => {
    if (v != null && v !== "") params.set(k, v);
  });
  return params.toString();
}

export function materialidadCounterpartyFolderZipUrl(counterpartyId, periodQuery = {}) {
  const qs = materialidadPeriodQueryString(periodQuery);
  return `/api/tramites/materialidad/counterparties/${encodeURIComponent(counterpartyId)}/export/folder.zip${qs ? `?${qs}` : ""}`;
}

export function materialidadCounterpartyFolderResumenPdfUrl(counterpartyId, periodQuery = {}) {
  const qs = materialidadPeriodQueryString(periodQuery);
  return `/api/tramites/materialidad/counterparties/${encodeURIComponent(counterpartyId)}/export/resumen.pdf${qs ? `?${qs}` : ""}`;
}

export function materialidadCounterpartyFolderCartaPdfUrl(counterpartyId, periodQuery = {}) {
  const qs = materialidadPeriodQueryString(periodQuery);
  return `/api/tramites/materialidad/counterparties/${encodeURIComponent(counterpartyId)}/export/carta.pdf${qs ? `?${qs}` : ""}`;
}

export function materialidadOperationFolderZipUrl(caseId) {
  return `/api/tramites/materialidad/operations/${encodeURIComponent(caseId)}/export/folder.zip`;
}

export function materialidadOperationFolderResumenPdfUrl(caseId) {
  return `/api/tramites/materialidad/operations/${encodeURIComponent(caseId)}/export/resumen.pdf`;
}

export function materialidadOperationFolderCartaPdfUrl(caseId) {
  return `/api/tramites/materialidad/operations/${encodeURIComponent(caseId)}/export/carta.pdf`;
}

export async function prepareMaterialidadCounterpartyEmail(counterpartyId, periodQuery = {}, toEmail) {
  const params = {
    ...normalizeMaterialidadPeriodParams(periodQuery),
  };
  if (toEmail) params.to_email = toEmail;
  const { data } = await api.get(
    `/tramites/materialidad/counterparties/${encodeURIComponent(counterpartyId)}/export/prepare-email`,
    { params },
  );
  return data;
}

export async function prepareMaterialidadOperationEmail(caseId, toEmail) {
  const params = {};
  if (toEmail) params.to_email = toEmail;
  const { data } = await api.get(
    `/tramites/materialidad/operations/${encodeURIComponent(caseId)}/export/prepare-email`,
    { params },
  );
  return data;
}

export async function sendMaterialidadCounterpartyEmail(counterpartyId, payload = {}) {
  const body = {
    to_email: payload.toEmail,
    attach_zip: payload.attachZip !== false,
    dry_run: !!payload.dryRun,
    ...normalizeMaterialidadPeriodParams(payload.periodQuery || {}),
  };
  const { data } = await api.post(
    `/tramites/materialidad/counterparties/${encodeURIComponent(counterpartyId)}/export/send-email`,
    body,
    { timeout: 180000 },
  );
  return data;
}

export async function sendMaterialidadOperationEmail(caseId, payload = {}) {
  const body = {
    to_email: payload.toEmail,
    attach_zip: payload.attachZip !== false,
    dry_run: !!payload.dryRun,
  };
  const { data } = await api.post(
    `/tramites/materialidad/operations/${encodeURIComponent(caseId)}/export/send-email`,
    body,
    { timeout: 180000 },
  );
  return data;
}

/** Sugerencias preventivas (Fase D) — solo lectura. */

export async function fetchMaterialidadSuggestions(counterpartyId, periodQuery = {}) {
  const params = normalizeMaterialidadPeriodParams(periodQuery);
  const { data } = await api.get(
    `/tramites/materialidad/counterparties/${encodeURIComponent(counterpartyId)}/suggestions`,
    { params },
  );
  return data;
}

export async function fetchMaterialidadOperationSuggestions(caseId) {
  const { data } = await api.get(
    `/tramites/materialidad/operations/${encodeURIComponent(caseId)}/suggestions`,
  );
  return data;
}
